import queryString from 'query-string';

import type { StringifyOptions } from 'query-string';

// Shared URL and error handling for the fetch-based Recruit API clients
// (`RecruitApiClient`, `NewUI/Job/API`).

export type ApiErrorPayload = Record<string, unknown> & { code: number };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Appends `params` to `path`. A path can already carry a query (`/fields?job_id=1`),
// so the separator is `&` in that case. An empty query adds no `?`.
export const buildApiUrl = (
  path: string,
  params?: unknown,
  options: StringifyOptions = {}
): string => {
  const query = isRecord(params)
    ? queryString.stringify(params, { arrayFormat: 'bracket', ...options })
    : '';
  if (!query) return path;

  return `${path}${path.includes('?') ? '&' : '?'}${query}`;
};

// Callers read the server keys (`error`, `errors`, `message`) directly on the thrown
// value, so the JSON payload is spread, not wrapped. A proxy HTML page or an empty
// body is not JSON; the error then carries only the HTTP status.
export const parseErrorResponse = async (response: Response): Promise<ApiErrorPayload> => {
  const payload: unknown = await response.json().catch(() => null);

  return isRecord(payload) ? { ...payload, code: response.status } : { code: response.status };
};

const messagesOf = (value: unknown): string[] => {
  if (typeof value === 'string') return value === '' ? [] : [value];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string' && entry !== '');
};

// Rails `errors.to_hash` (`{ title: ["can't be blank"] }`), or `{ fields, base }` as
// Admin::AccountSettingsController renders it.
const fieldSource = (errors: Record<string, unknown>): Record<string, unknown> =>
  isRecord(errors.fields) ? errors.fields : errors;

/** Field name -> its first server message, for a form to show beside each field. */
export const apiFieldErrors = (error: unknown): Record<string, string> => {
  if (!isRecord(error) || !isRecord(error.errors)) return {};
  return Object.fromEntries(
    Object.entries(fieldSource(error.errors)).flatMap(([field, value]) => {
      const [message] = messagesOf(value);
      return message ? [[field, message]] : [];
    })
  );
};

const fieldLabel = (field: string): string => {
  const words = field.replace(/[._]/g, ' ');
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

// As Rails full messages: "Title can't be blank"; `base` messages stand alone.
const recordMessages = (errors: Record<string, unknown>): string[] =>
  Object.entries(errors).flatMap(([field, value]) => {
    if (field === 'fields' && isRecord(value)) return recordMessages(value);
    if (field === 'base') return messagesOf(value);
    const [message] = messagesOf(value);
    return message ? [`${fieldLabel(field)} ${message}`] : [];
  });

/**
 * The one message to show for a failed request: the server's `error` (or `error.message`),
 * then `errors` as a string, a list or field messages, then `message` (an `Error`'s too),
 * else the page's own `fallback` wording.
 */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  if (!isRecord(error)) return fallback;
  const { error: detail, errors, message } = error;
  if (typeof detail === 'string' && detail !== '') return detail;
  if (isRecord(detail) && typeof detail.message === 'string' && detail.message !== '') {
    return detail.message;
  }
  const listed = isRecord(errors) ? recordMessages(errors) : messagesOf(errors);
  if (listed.length > 0) return listed.join(', ');
  if (typeof message === 'string' && message !== '') return message;
  return fallback;
};
